import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined'

import { Divider, Paper } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { AnaliseRow } from '../../analise.types'
import AnaliseSelectScope from '../analise_select.scopes'

const LOG = Logger.get('ANALISE-SELECT-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type AnaliseSelectViewProps = {
    className?: string
    scope: AnaliseSelectScope
}
class AnaliseSelectViewClass implements FCClassContext<AnaliseSelectViewProps> {
    // :: Fields
    scope!: AnaliseSelectScope
    sytles!: ReturnType<typeof useStyles>
    analiseColumns!: GridColDef[]
    nomeAnaliseInputRef!: React.RefObject<HTMLInputElement | null>

    // :: emissors

    readonly emitOnSelect = () => {
        this.scope.onSelect().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitOnFind = () => {
        this.scope.nomeAnaliseInputRef = this.nomeAnaliseInputRef.current?.value || ''
        this.scope.onFind().catch(LOG.caught)
    }

    readonly handleSelectAnaliseClick = (id: GridRowId) => () => {
        this.scope.idSelected = id as number
        this.scope.onSelect().catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: AnaliseSelectViewProps) {
        this.scope = scope
        this.sytles = useStyles()
        this.analiseColumns = static_build_analise_columns(this.handleSelectAnaliseClick)
    }

    render({ className, scope }: AnaliseSelectViewProps) {
        this.scope = bindUpdate(React, scope)
        this.nomeAnaliseInputRef = React.useRef<HTMLInputElement>(null)

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'baseline',
                        gap: '10px'
                    }}
                >
                    <TextField
                        className={classes.cnpjInput}
                        inputRef={this.nomeAnaliseInputRef}
                        label="Filtre pelo nome da análise"
                        variant="standard"
                    />
                    <Button variant="outlined" onClick={this.emitOnFind}>
                        Buscar
                    </Button>
                </Box>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <DataGrid
                        rows={this.scope.analises}
                        columns={this.analiseColumns}
                        getRowId={static_getAnaliseRowId}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                    />
                </Paper>

                <Divider />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'baseline',
                        gap: '10px',
                        marginTop: '10px'
                    }}
                >
                    <Button variant="outlined" onClick={this.emitOnClose}>
                        Cancelar
                    </Button>
                </Box>
            </div>
        )
    }
}

export const AnaliseSelectView = classToFComponent(AnaliseSelectViewClass, React)
export default AnaliseSelectView

function static_build_analise_columns(handleSelectClick: (id: GridRowId) => () => void): GridColDef[] {
    return [
        {
            field: 'tipo',
            headerName: 'Tipo',
            width: 100,
            valueFormatter: (value: string) => {
                switch (value) {
                    case 'CEDENTE_SACADO':
                        return 'Cedente/Sacado'
                    case 'CEDENTE':
                        return 'Cedente'
                    case 'SACADO':
                        return 'Sacado'
                    case 'TITULO':
                        return 'Título'
                    case 'NFE':
                        return 'Nfe'
                    case 'LOTE_CNPJ_CPF':
                        return 'Lote CNPJ/CPF'
                    default:
                        return ''
                }
            }
        },
        {
            field: 'nome',
            headerName: 'Nome',
            width: 300
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'select' + id}
                        icon={<AddCircleOutlineIcon />}
                        label="Add"
                        className="textPrimary"
                        onClick={handleSelectClick(id)}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_getAnaliseRowId(row: AnaliseRow) {
    return row.id + ''
}
